import React from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  IconButton,
  Grid,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  TextField,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import moment from "moment";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useUsers, useMultipleActiveTenancies } from "services/hooks/rentals";
import { baseURL } from "services/API";
import { setTenantOccupancy } from "state";
import TenantsTable from "components/Tables/TenantsTable";
import TenantsPopover from "components/PopOvers/TenantsPopover";
import FormDialog from "components/modal";
import TopSnack from "components/TopSnack";
import FlexBetween from "components/FlexBetween";
import { parseApprovalResponse, apiErrorMessage } from "utilities/approvalResponse";

const MULTIPLE_TAB_INDEX = 3;

const TENANT_USER_TABS = [
  {
    userFilter: 2,
    label: "Never onboarded",
    description:
      "Self-registered tenants with no occupancy and no active period — signed up but never placed in a tenancy.",
  },
  {
    userFilter: 0,
    label: "No occupancy",
    description: "Active tenants with no created occupancy record.",
  },
  {
    userFilter: 1,
    label: "With tenancy",
    description: "Tenants with at least one created occupancy.",
  },
  {
    type: "multiple",
    label: "Multiple active tenancies",
    description:
      "Active occupancies for tenants who currently hold more than one active tenancy.",
  },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${TableCell.head}`]: {
    backgroundColor: "#FEF0DE",
    color: theme.palette.common.black,
    fontSize: 16,
    fontWeight: "bold",
  },
  [`&.${TableCell.body}`]: {
    fontSize: 16,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const TitleContainer = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const endTenancySchema = yup.object().shape({
  unit_id: yup.number().required("This is required"),
  tenant_id: yup.number().required("This is required"),
  date_ended: yup.date().required("This is required"),
  reason_ended: yup.string().required("This is required"),
});

const TenantsPage = () => {
  const token = useSelector((state) => state.token);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

  const [activeTabIndex, setActiveTabIndex] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);
  const [multiplePage, setMultiplePage] = React.useState(0);
  const [multipleRowsPerPage, setMultipleRowsPerPage] = React.useState(20);

  const [open, setOpen] = React.useState(false);
  const [modal, setModal] = React.useState("");
  const [details, setDetails] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [endTenancyData, setEndTenancyData] = React.useState({
    unit_id: "",
    tenant_id: "",
    date_ended: moment().format("YYYY-MM-DD"),
    reason_ended: "",
  });
  const [errors, setErrors] = React.useState({});
  const [openSnack, setOpenSnack] = React.useState({
    open: false,
    vertical: "top",
    horizontal: "center",
  });
  const [severity, setSeverity] = React.useState("");
  const [message, setMessage] = React.useState("");

  const isMultipleTab = activeTabIndex === MULTIPLE_TAB_INDEX;
  const activeTabMeta = TENANT_USER_TABS[activeTabIndex];
  const userFilter = activeTabMeta?.userFilter ?? 2;

  const { users, pagination, loading, error } = useUsers({
    userFilter,
    page: page + 1,
    pageSize: rowsPerPage,
    enabled: !isMultipleTab,
  });

  const {
    rows: multipleRows,
    summary: multipleSummary,
    pagination: multiplePagination,
    loading: multipleLoading,
    error: multipleError,
    refetch: refetchMultiple,
  } = useMultipleActiveTenancies({
    page: multiplePage + 1,
    pageSize: multipleRowsPerPage,
    enabled: isMultipleTab,
  });

  const openPopper = Boolean(anchorEl);
  const popperId = openPopper ? "users-multiple-tenancies-popover" : undefined;

  const handleTabChange = (_, newIndex) => {
    setActiveTabIndex(newIndex);
    setPage(0);
    setMultiplePage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMultipleChangePage = (event, newPage) => {
    setMultiplePage(newPage);
  };

  const handleMultipleChangeRowsPerPage = (event) => {
    setMultipleRowsPerPage(parseInt(event.target.value, 10));
    setMultiplePage(0);
  };

  const usernameOrPhone = (user) => {
    if (user.username) return user.username;
    if (user.phone_number != null) return String(user.phone_number);
    return "—";
  };

  const formatDateAdded = (date) => (date ? moment(date).format("DD MMM YYYY") : "—");

  const handleClickPopOver = (event, row) => {
    setAnchorEl(event.currentTarget);
    setDetails(row);
  };

  const handleClosePopOver = () => {
    setAnchorEl(null);
  };

  const handleNavigateTenantPayments = (occupancy) => {
    dispatch(setTenantOccupancy(occupancy));
    navigate("/tenant-payments");
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setModal("");
    setOpen(false);
    setEndTenancyData({
      unit_id: "",
      tenant_id: "",
      date_ended: moment().format("YYYY-MM-DD"),
      reason_ended: "",
    });
  };

  const handleModalView = (modalView) => {
    setModal(modalView);
  };

  const handleChangeEndTenancyData = (event) => {
    const { name, value } = event.target;
    setEndTenancyData((prev) => ({ ...prev, [name]: value }));
    if (!value.trim()) {
      setErrors((prev) => ({ ...prev, [name]: "This is required" }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnack({ ...openSnack, open: false });
  };

  const handleSubmitEndTenancy = async () => {
    try {
      await endTenancySchema.validate(
        {
          unit_id: details.related_rental_unit.id,
          tenant_id: details.related_tenant.id,
          date_ended: endTenancyData.date_ended,
          reason_ended: endTenancyData.reason_ended,
        },
        { abortEarly: false }
      );
    } catch (validationError) {
      const newError = {};
      validationError.inner.forEach((err) => {
        newError[err.path] = err.message;
      });
      setErrors(newError);
      return;
    }

    try {
      const response = await axios.post(`${baseURL}/landlords/rentals/units/remove`, {
        unit_id: details.related_rental_unit.id,
        tenant_id: details.related_tenant.id,
        date_ended: endTenancyData.date_ended,
        reason_ended: endTenancyData.reason_ended,
      });
      const approval = parseApprovalResponse(response);

      handleClose();
      setErrors({});
      setOpenSnack({ ...openSnack, open: true });
      if (approval.pendingApproval) {
        setSeverity("info");
        setMessage(
          approval.message ||
            `End tenancy submitted for approval (request #${approval.approvalRequestId}). Check Audit & Approvals.`
        );
      } else {
        refetchMultiple();
        handleClosePopOver();
        setSeverity("success");
        setMessage("Tenancy ended successfully.");
      }
    } catch (submitError) {
      setOpenSnack({ ...openSnack, open: true });
      setSeverity("error");
      setMessage(apiErrorMessage(submitError, "Failed to end tenancy."));
    }
  };

  const renderDescription = () => {
    if (isMultipleTab) {
      const tenantCount = multipleSummary.total_tenants_with_multiple_active_tenancies ?? 0;
      const rowCount = multipleSummary.total_occupancy_rows ?? multiplePagination.total ?? 0;
      return (
        <>
          {activeTabMeta.description}
          {rowCount > 0 && (
            <>
              {" "}
              — {tenantCount} tenant{tenantCount === 1 ? "" : "s"} across {rowCount} active occupanc
              {rowCount === 1 ? "y" : "ies"}.
            </>
          )}
        </>
      );
    }

    return (
      <>
        {activeTabMeta.description}
        {pagination.total > 0 && (
          <> — {pagination.total} user{pagination.total === 1 ? "" : "s"} total.</>
        )}
      </>
    );
  };

  const currentLoading = isMultipleTab ? multipleLoading : loading;
  const currentError = isMultipleTab ? multipleError : error;

  return (
    <Box m="20px">
      <Grid container spacing={2} style={{ marginTop: "20px" }}>
        <Grid item xs={12}>
          <TitleContainer>
            <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "25px" }}>
              Users
            </Typography>
          </TitleContainer>
        </Grid>
      </Grid>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={activeTabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TENANT_USER_TABS.map((tab, index) => (
            <Tab key={tab.label} label={tab.label} value={index} />
          ))}
        </Tabs>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {renderDescription()}
      </Typography>

      {currentLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {currentError && !currentLoading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {typeof currentError === "string" ? currentError : "Error loading tenants."}
        </Alert>
      )}

      {!currentLoading && !currentError && !isMultipleTab && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>Tenant Number</StyledTableCell>
                <StyledTableCell>First Name</StyledTableCell>
                <StyledTableCell>Last Name</StyledTableCell>
                <StyledTableCell>Username (phone number)</StyledTableCell>
                <StyledTableCell>Date Added</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No users in this category.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <StyledTableRow key={user.id}>
                    <StyledTableCell>{user.tenant_number ?? "—"}</StyledTableCell>
                    <StyledTableCell>{user.first_name ?? "—"}</StyledTableCell>
                    <StyledTableCell>{user.last_name || "—"}</StyledTableCell>
                    <StyledTableCell>{usernameOrPhone(user)}</StyledTableCell>
                    <StyledTableCell>{formatDateAdded(user.date_added)}</StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={pagination.total ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
          />
        </TableContainer>
      )}

      {!currentLoading && !currentError && isMultipleTab && (
        <>
          <TenantsTable
            rows={multipleRows}
            showProperty
            hidePagination
            handleClickPopOver={handleClickPopOver}
            handleNavigateTenantPayments={handleNavigateTenantPayments}
            id={popperId}
          />
          <Paper>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={multiplePagination.total ?? 0}
              rowsPerPage={multipleRowsPerPage}
              page={multiplePage}
              onPageChange={handleMultipleChangePage}
              onRowsPerPageChange={handleMultipleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
            />
          </Paper>

          <TenantsPopover
            popperId={popperId}
            openPopper={openPopper}
            anchorEl={anchorEl}
            handleClickOpen={handleClickOpen}
            handleClosePopOver={handleClosePopOver}
            details={details}
            handleNavigateTenantPayments={handleNavigateTenantPayments}
            handleModalView={handleModalView}
            limitedActions
          />

          {modal === "delete" && (
            <FormDialog
              title="End Tenancy"
              enableActions
              open={open}
              content={
                <>
                  <Typography color="red">
                    Please input the following details to confirm the end of the tenancy
                  </Typography>
                  <TextField
                    required
                    label="Reason for Ending Tenancy"
                    name="reason_ended"
                    value={endTenancyData.reason_ended}
                    multiline
                    rows={3}
                    onChange={handleChangeEndTenancyData}
                    fullWidth
                    margin="dense"
                    error={Boolean(errors.reason_ended)}
                    helperText={errors.reason_ended}
                    autoFocus
                  />
                  <FlexBetween>
                    <Typography>Date of Tenancy End:</Typography>
                    <TextField
                      required
                      name="date_ended"
                      type="date"
                      label="Tenancy End Date"
                      value={endTenancyData.date_ended}
                      onChange={handleChangeEndTenancyData}
                      sx={{ width: "50%" }}
                      margin="dense"
                      error={Boolean(errors.date_ended)}
                      helperText={errors.date_ended}
                    />
                  </FlexBetween>
                </>
              }
              handleSubmit={handleSubmitEndTenancy}
              handleClose={handleClose}
              buttonText="Yes, End Tenancy"
            />
          )}
        </>
      )}

      <TopSnack
        vertical={openSnack.vertical}
        horizontal={openSnack.horizontal}
        open={openSnack.open}
        handleCloseSnack={handleCloseSnack}
        message={message}
        severity={severity}
      />
    </Box>
  );
};

export default TenantsPage;
